import { callFn } from "@workspace/admin-ui";

export interface MembershipRow {
  membershipId: string;
  uid: string;
  role: string;
  chainId: string;
  pharmacyId: string;
  email: string;
  displayName: string;
  status: string;
  createdAt: string | null;
  suspendedAt: string | null;
  suspendedReason: string;
  psiNumber: string;
}

export interface InviteRow {
  id: string;
  email: string;
  displayName: string;
  role: string;
  chainId: string;
  pharmacyId: string;
  membershipId: string;
  status: string;
  createdAt: string | null;
  createdByUid: string;
}

export const pharmacyAdminApi = {
  listPharmacists: (data: { status?: "active" | "suspended" | "all" }) =>
    callFn<typeof data, { items: MembershipRow[] }>(
      "pharmacyAdminListMemberships",
      data,
    ),

  invitePharmacist: (data: {
    email: string;
    displayName: string;
    psiNumber: string;
  }) =>
    callFn<
      typeof data,
      {
        ok: true;
        uid: string;
        membershipId: string;
        email: string;
        inviteLink: string;
        emailSent: boolean;
      }
    >("pharmacyAdminInvitePharmacist", data),
  issuePharmacistInvite: (data: {
    email: string;
    displayName: string;
    psiNumber?: string;
    expiresDays?: number;
  }) =>
    callFn<
      typeof data,
      {
        ok: true;
        inviteId: string;
        token: string;
        inviteLink: string;
        expiresAt: string;
        email: string;
        uid: string;
        emailSent: boolean;
        psiNumber: string;
      }
    >("pharmacyAdminIssuePharmacistInvite", data),

  updatePharmacist: (data: {
    membershipId: string;
    displayName?: string;
    psiNumber?: string;
  }) =>
    callFn<typeof data, { ok: true; membershipId: string }>(
      "pharmacyAdminUpdatePharmacist",
      data,
    ),

  suspendMembership: (data: { membershipId: string; reason?: string }) =>
    callFn<typeof data, { ok: true; uid: string; membershipId: string }>(
      "pharmacyAdminSuspendMembership",
      data,
    ),

  reactivateMembership: (data: { membershipId: string }) =>
    callFn<typeof data, { ok: true; uid: string; membershipId: string }>(
      "pharmacyAdminReactivateMembership",
      data,
    ),

  revokeMembership: (data: {
    membershipId: string;
    reason?: string;
    disableAuth?: boolean;
  }) =>
    callFn<
      typeof data,
      {
        ok: true;
        uid: string;
        membershipId: string;
        historyId: string;
        authDisabled: boolean;
        authError: string | null;
      }
    >("pharmacyAdminRevokeMembership", data),

  listInvites: () =>
    callFn<Record<string, never>, { items: InviteRow[] }>(
      "pharmacyAdminListInvites",
      {},
    ),

  resendInvite: (data: { inviteId: string }) =>
    callFn<typeof data, { ok: true; email: string; inviteLink: string }>(
      "pharmacyAdminResendInvite",
      data,
    ),

  dismissInvite: (data: { inviteId: string }) =>
    callFn<typeof data, { ok: true }>("pharmacyAdminDismissInvite", data),
};

export interface BookingPharmacy {
  id: string;
  name: string;
  address: string;
  eircode: string;
  // True for the oldest booking-pharmacy record for this tenant pharmacy —
  // the one every other surface (mobile app, patient booking link) treats
  // as canonical when more than one exists. See
  // functions/src/lib/bookingPharmacyLink.ts.
  isPrimary?: boolean;
}

export interface Room {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string | null;
}

export interface AvailabilityWindow {
  dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  startTime: string;
  endTime: string;
}

export interface BlockoutDate {
  date: string;
  reason?: string;
}

export interface Availability {
  windows: AvailabilityWindow[];
  blockoutDates: BlockoutDate[];
  updatedAt: string | null;
}

export interface Booking {
  id: string;
  roomId: string;
  pharmacistId: string | null;
  condition: string | null;
  date: string;
  startTime: string;
  endTime: string;
  status: string;
  patientEmail: string;
  notes: string;
  consultationId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export const pharmacyAdminBookingsApi = {
  listBookingPharmacies: () =>
    callFn<Record<string, never>, { items: BookingPharmacy[] }>(
      "pharmacyAdminListBookingPharmacies",
      {},
    ),

  listRooms: (data: { bookingPharmacyId: string }) =>
    callFn<typeof data, { items: Room[] }>("pharmacyAdminListRooms", data),

  listAvailabilityPharmacists: (data: { bookingPharmacyId: string }) =>
    callFn<typeof data, { items: Array<{ pharmacistId: string; displayName: string }> }>(
      "pharmacyAdminListAvailabilityPharmacists",
      data,
    ),

  getAvailability: (data: { bookingPharmacyId: string; pharmacistId: string }) =>
    callFn<typeof data, { availability: Availability | null }>(
      "pharmacyAdminGetAvailability",
      data,
    ),

  setAvailability: (data: {
    bookingPharmacyId: string;
    pharmacistId: string;
    windows: AvailabilityWindow[];
    blockoutDates: BlockoutDate[];
  }) =>
    callFn<typeof data, { ok: true }>("pharmacyAdminSetAvailability", data),

  listBookings: (data: {
    bookingPharmacyId: string;
    status?:
      | "all"
      | "pending"
      | "confirmed"
      | "cancelled"
      | "rescheduled"
      | "completed"
      | "no_show";
    pharmacistId?: string;
    startDate?: string;
    endDate?: string;
  }) => callFn<typeof data, { items: Booking[] }>("pharmacyAdminListBookings", data),

  updateBookingStatus: (data: {
    bookingPharmacyId: string;
    bookingId: string;
    status: "cancelled" | "completed" | "no_show" | "confirmed";
  }) =>
    callFn<typeof data, { ok: true; bookingId: string; status: string }>(
      "pharmacyAdminUpdateBookingStatus",
      data,
    ),

  rescheduleBooking: (data: {
    bookingPharmacyId: string;
    bookingId: string;
    newDate: string;
    newStartTime: string;
    newEndTime: string;
  }) => callFn<typeof data, { ok: true; bookingId: string }>("pharmacyAdminRescheduleBooking", data),
};
